import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, CheckCircle2, Loader2, Play, RefreshCw } from 'lucide-react'
import { listJobs, listLimitStatus, runJob, type BillingJob } from '@/api/billingAdmin'
import { InfoBox, Td, Th, chipStyle, errText, tableStyle } from '../adminUi'

const JOB_HELP: Record<string, string> = {
  limits_scan: 'Counts each application\'s tables, screens and records and stamps the over-limit flags request paths read.',
  subscription_sweep: 'Sends expiry reminders, reconciles renewals the webhook missed, applies grace and lapse.',
  credit_maintenance: 'Expires monthly credit grants at period end and fills in any monthly grant a lost webhook never delivered.',
  payment_reconciliation: 'Checks stale orders against the gateway and retries webhook events that errored.',
}

function ago(iso: string | null): string {
  if (!iso) return 'never'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

export default function JobsPanel() {
  const [jobs, setJobs] = useState<BillingJob[]>([])
  const [overLimit, setOverLimit] = useState<Awaited<ReturnType<typeof listLimitStatus>>['projects']>([])
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [j, l] = await Promise.all([listJobs(), listLimitStatus(true).catch(() => ({ projects: [] }))])
      setJobs(j.jobs)
      setOverLimit(l.projects)
    } catch (e) {
      toast.error(errText(e, 'Could not load the job status'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const run = async (name: string) => {
    setRunning(name)
    try {
      const res = await runJob(name)
      setJobs(res.jobs)
      toast.success(`${name} finished`)
      await load()
    } catch (e) {
      toast.error(errText(e, `Could not run ${name}`))
    } finally {
      setRunning(null)
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" /></div>
  }

  const runnerOff = jobs.some((j) => !j.runner_enabled)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <InfoBox>
        These run in the background on exactly one server process, elected through
        a database lock — so a multi-worker deployment never double-sends a
        reminder or double-charges anything. Failover is automatic if that process
        dies.
        {runnerOff && (
          <div style={{ marginTop: '0.5rem', color: 'var(--color-warning, #f0a020)' }}>
            The in-process runner is disabled (<code>BILLING_JOBS_INLINE=0</code>).
            Jobs only run if a separate process is running them, or when triggered here.
          </div>
        )}
      </InfoBox>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>

      <table style={tableStyle}>
        <thead>
          <tr><Th>Job</Th><Th>Every</Th><Th>Last run</Th><Th>Status</Th><Th>Detail</Th><Th /></tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.job_name}>
              <Td>
                <div style={{ fontWeight: 500 }}>{j.job_name}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                  {JOB_HELP[j.job_name] || ''}
                </div>
              </Td>
              <Td>{j.interval_seconds >= 3600
                ? `${Math.round(j.interval_seconds / 3600)}h`
                : `${Math.round(j.interval_seconds / 60)}m`}</Td>
              <Td>{ago(j.last_run_at)}</Td>
              <Td>
                {j.last_status === 'ok' ? (
                  <span style={{ color: 'var(--color-success, #2e9e5b)', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    <CheckCircle2 size={12} /> ok
                  </span>
                ) : j.last_status === 'error' ? (
                  <span style={{ color: 'var(--color-danger, #d64545)', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    <AlertTriangle size={12} /> error
                  </span>
                ) : <span style={{ color: 'var(--color-text-secondary)' }}>—</span>}
              </Td>
              <Td style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                {Object.entries(j.detail || {}).slice(0, 4)
                  .map(([k, v]) => `${k}: ${String(v)}`).join(' · ') || '—'}
              </Td>
              <Td right>
                <button className="btn btn-secondary" style={{ padding: '0.15rem 0.5rem' }}
                        disabled={running === j.job_name} onClick={() => run(j.job_name)}>
                  {running === j.job_name ? <Loader2 size={12} className="spin" /> : <Play size={12} />} Run now
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      <div>
        <h3 style={{ fontSize: '1.0625rem', marginBottom: '0.5rem' }}>Applications over their plan limits</h3>
        {!overLimit.length ? (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
            None. (Applications whose owner's plan sets no limits are skipped by the
            scan entirely, so they never appear here.)
          </p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>Owner</Th><Th>Application</Th><Th>Plan</Th>
                <Th right>Tables</Th><Th right>Screens</Th><Th right>Records</Th>
                <Th>Over</Th><Th>Checked</Th>
              </tr>
            </thead>
            <tbody>
              {overLimit.map((p) => (
                <tr key={`${p.owner_user_id}/${p.project_id}`}>
                  <Td style={{ fontSize: '0.8125rem' }}>{p.owner_user_id}</Td>
                  <Td>{p.project_id}</Td>
                  <Td><span style={chipStyle}>{p.plan_code}</span></Td>
                  <Td right>{p.table_count ?? '—'}{p.max_tables !== null ? ` / ${p.max_tables}` : ''}</Td>
                  <Td right>{p.screen_count ?? '—'}{p.max_screens !== null ? ` / ${p.max_screens}` : ''}</Td>
                  <Td right>{p.record_count?.toLocaleString() ?? '—'}{p.max_records !== null ? ` / ${p.max_records.toLocaleString()}` : ''}</Td>
                  <Td>
                    {[p.over_tables && 'tables', p.over_screens && 'screens', p.over_records && 'records']
                      .filter(Boolean).join(', ')}
                  </Td>
                  <Td>{ago(p.computed_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
